import { uploadApis } from "@payping/api";
import { Dispatch, FC, SetStateAction, useCallback, useEffect, useState } from "react";
import { Input, LegacyForm, Modal, Select, Space, Tooltip, Upload, useMessagedAsyncFn } from "@payping/web-components";
import { Add, QuestionCircleOutlined } from "@payping/icons";
import { ValidUrl, ValidUrlWithLocalhost } from "@payping/utils/validation";
import { createClientApp, editClientApp, getClientAppList } from "@payping/redux/actions";
import type { UploadChangeParam, UploadFile } from "@payping/web-components/components/upload/types";

import { useAppDispatch } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { isLocalIp, validIpOrRange } from "@/utils/validIp";
import { ConfirmModalProps } from "@/components/DashIpg/ConfirmModal";
import { OauthType } from "./OauthTypes";
import { SwitchRow } from "@/core/component";
import { TOKEN_SCOPES } from "@/core/consts";

interface FormRef {
  formModalData: OauthType;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setConfirmModalOpen: Dispatch<SetStateAction<boolean>>;
  setConfirmModalData: Dispatch<SetStateAction<ConfirmModalProps | undefined>>;
}

interface FormType {
  logoUri?: string | UploadChangeParam;
  isTest?: boolean;
  clientName?: string;
  clientUri?: string;
  allowedIPs?: string[];
  expireMonths?: number;
  scopes?: string[];
  redirectUris: string[];
  description?: string;
}

export const OauthFormModal: FC<FormRef> = (props: FormRef) => {
  // =========================================
  // Init
  // =========================================
  const { formModalData, open, setOpen, setConfirmModalData, setConfirmModalOpen } = props;
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const [fileList, setFileList] = useState<UploadFile[]>(
    formModalData.logoUri
      ? [
          {
            name: "image",
            size: 0,
            type: "image/*",
            uid: "0",
            url: formModalData.logoUri,
          },
        ]
      : [],
  );
  const [form] = LegacyForm.useForm<FormType>();
  const [hiddenScopes] = useState<string[]>(
    (formModalData?.allowedScopes || []).reduce(
      (acc: string[], scope) =>
        (TOKEN_SCOPES.ADMIN_SCOPES as unknown as string[]).includes(scope) ? [...acc, scope] : acc,
      [],
    ),
  );

  useEffect(() => {
    if (formModalData) {
      const { allowedScopes, ...data } = formModalData;
      form.setFieldsValue({
        ...data,
        scopes: allowedScopes?.length ? allowedScopes : ["pay:read", "pay:write"],
      });
    }
  }, [form, formModalData]);

  const hideModal = useCallback(() => {
    form.resetFields();
    setOpen(false);
  }, [form, setOpen]);

  const [{ loading: loadingSubmit }, onSubmit] = useMessagedAsyncFn(
    async (values: FormType) => {
      const { logoUri, scopes: formScopes, ...oauthForm } = values;

      if (formModalData.clientId) {
        await dispatch(
          editClientApp({
            ...oauthForm,
            clientId: formModalData.clientId,
            logo:
              typeof logoUri === "string"
                ? logoUri.split("/")[logoUri.split("/").length - 1]
                : logoUri?.fileList[0]?.response,
            scopes: formScopes?.filter((scope) => !hiddenScopes.includes(scope)),
          }),
        );
        hideModal();
      } else {
        const resultClient = await dispatch(
          createClientApp({
            ...oauthForm,
            logo:
              typeof logoUri === "string"
                ? logoUri.split("/")[logoUri.split("/").length - 1]
                : logoUri?.fileList[0]?.response,
            scopes: formScopes?.filter((scope) => !hiddenScopes.includes(scope)),
          }),
        );

        await dispatch(getClientAppList());

        setConfirmModalData({
          client: { backToList: true, clientId: resultClient.clientId, clientSecret: resultClient.clientSecret },
          showGuide: !resultClient.isTest,
        });
        setConfirmModalOpen(true);
      }
      hideModal();
    },
    [dispatch, formModalData.clientId, hiddenScopes, hideModal, setConfirmModalData, setConfirmModalOpen],
    undefined,
    { useProgress: true },
  );

  // =========================================
  // Render
  // =========================================
  return (
    <Modal
      title={formModalData.clientId ? t("oauth.editOauth") : t("oauth.addOauth")}
      mask={{ closable: false }}
      open={open}
      okText={formModalData.clientId ? t("oauth.editOauth") : t("oauth.addOauth")}
      onOk={() => form.submit()}
      cancelText={t("return")}
      onCancel={() => hideModal()}
    >
      <LegacyForm<FormType> form={form} onFinish={onSubmit} layout={"vertical"}>
        <LegacyForm.Item<FormType> name="logoUri">
          <Upload
            listType={"picture-card"}
            requestFunction={uploadApis.miscWithFilePost}
            onChange={(info: UploadChangeParam): void => setFileList(info.fileList)}
            fileList={fileList}
            showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
            allowedExtensions={["jpg", "jpeg", "png"]}
            disabled={loadingSubmit}
          >
            {fileList.length ? null : (
              <div>
                <Add width={"14px"} className={"tw:color-primary"} />
                <div className={"ant-upload-text"}>{t("uploadImage")}</div>
              </div>
            )}
          </Upload>
        </LegacyForm.Item>
        <LegacyForm.Item name="isTest" hidden={Boolean(formModalData.clientId)} valuePropName={"checked"}>
          <SwitchRow
            loading={loadingSubmit}
            label={
              <Tooltip
                placement="leftBottom"
                title={
                  <ul>
                    <li key={0}>{t("oauth.attention1")}</li>
                    <br />
                    <li key={1}>{t("oauth.attention2")}</li>
                    <br />
                    <li key={1}>{t("oauth.attention3")}</li>
                    <br />
                    <li key={1}>{t("oauth.attention4")}</li>
                  </ul>
                }
              >
                <Space>
                  {t("oauth.testOauth")}
                  <QuestionCircleOutlined width={"14px"} />
                </Space>
              </Tooltip>
            }
            checkedChildren={t("active")}
            unCheckedChildren={t("inactive")}
          />
        </LegacyForm.Item>
        <LegacyForm.Item label={t("oauth.clientName")} name="clientName" rules={[{ required: true }]}>
          <Input disabled={loadingSubmit} />
        </LegacyForm.Item>
        <LegacyForm.Url<FormType>
          form={form}
          skeleton={loadingSubmit}
          name={"clientUri"}
          placeholder={"www.payping.ir"}
          label={t("url")}
          rules={[
            { required: true },
            ({ getFieldValue }) => ({
              validator(): Promise<void> {
                const url = getFieldValue("clientUri");
                const isTest = getFieldValue("isTest");
                return url && (isTest ? !ValidUrlWithLocalhost(url) : !ValidUrl(url))
                  ? Promise.reject(t("error.url"))
                  : Promise.resolve();
              },
            }),
          ]}
        />
        <LegacyForm.Item
          label={t("oauth.allowedIps")}
          name="allowedIPs"
          dependencies={["scopes"]}
          tooltip={t("token.allowedIpsHelp")}
          rules={[
            ({ getFieldValue }) => ({
              required: ((getFieldValue("scopes") as string[]) || []).some(
                (scope) => scope.includes("pay") || scope.includes("write"),
              ),
            }),
            () => ({
              validator(_rule, value: string[]): Promise<void> {
                const invalidIp = (value || []).find((ip) => !validIpOrRange(ip));
                if (invalidIp) {
                  return Promise.reject(t("error.ip"));
                }
                const localIp = (value || []).find((ip) => isLocalIp(ip));
                return localIp ? Promise.reject(t("error.localIp")) : Promise.resolve();
              },
            }),
          ]}
        >
          <Select className={"tw:dir-ltr!"} placeholder={"8.8.8.8"} mode="tags" disabled={loadingSubmit} />
        </LegacyForm.Item>
        <LegacyForm.Item label={t("oauth.expireMonths")} name="expireMonths" rules={[{ required: true }]}>
          <Input disabled={loadingSubmit} />
        </LegacyForm.Item>
        <LegacyForm.Item label={t("oauth.scopes")} name="scopes" rules={[{ required: true }]}>
          <Select
            disabled={loadingSubmit}
            mode="multiple"
            allowClear
            filterOption={(_, option) => (option?.value ? !hiddenScopes.includes(String(option?.value)) : false)}
          >
            <Select.OptGroup label={t("oauth.personalInfo")}>
              {TOKEN_SCOPES.IDENTITY_SCOPES.map((scope) => (
                <Select.Option key={scope} value={scope}>
                  {t(`scopes.${scope.replace(":", "")}`)}
                </Select.Option>
              ))}
            </Select.OptGroup>
            <Select.OptGroup label={t("oauth.applicationInfo")}>
              {TOKEN_SCOPES.USER_SCOPES.map((scope) => (
                <Select.Option key={scope} value={scope}>
                  {t(`scopes.${scope.replace(":", "")}`)}
                </Select.Option>
              ))}
            </Select.OptGroup>
            <Select.OptGroup label={t("oauth.specialScopes")}>
              {hiddenScopes.map((scope) => (
                <Select.Option key={scope} value={scope} disabled>
                  {t(`scopes.${scope.replace(":", "")}`)}
                </Select.Option>
              ))}
            </Select.OptGroup>
          </Select>
        </LegacyForm.Item>
        <LegacyForm.Item
          label={t("oauth.returnUrl")}
          name="redirectUris"
          rules={[
            { required: true },
            ({ getFieldValue }) => ({
              validator(_rule, value): Promise<void> {
                const getError = () => {
                  const isTest = getFieldValue("isTest");
                  for (let i = 0; i < value.length; i += 1) {
                    if (isTest ? !ValidUrlWithLocalhost(value[i]) : !ValidUrl(value[i])) return t("error.url");
                  }
                  return undefined;
                };
                const error = getError();
                return value && error ? Promise.reject(error) : Promise.resolve();
              },
            }),
          ]}
        >
          <Select placeholder="https://www.payping.ir" mode="tags" disabled={loadingSubmit} />
        </LegacyForm.Item>
        <LegacyForm.Item label={t("description")} name="description" rules={[{ required: true }]}>
          <Input.TextArea disabled={loadingSubmit} />
        </LegacyForm.Item>
      </LegacyForm>
    </Modal>
  );
};
